/**
 * MGC-S360 · Cliente Sprints360 (backend real, autenticado)
 *
 * Interface única para todas as 6 entidades da fundação Sprints360:
 *   - Atividades, Dependências, Participantes, Rituais, Atas, Documentos
 *
 * Usa axios + token Supabase. Todos os métodos são async, retornam
 * o payload cru da API (documentado nos routers do backend).
 *
 * Design intencional:
 *   • Nenhum cache global — chamadas são feitas sob demanda; o consumidor
 *     decide se guarda em useState/React Query.
 *   • Erros de rede são propagados (para o consumidor tratar exibição).
 *   • projetoId é obrigatório em listagens (backend impõe).
 *
 * Padrão de uso mínimo:
 *   import { useSprints360Api } from "@/lib/sprints360Api";
 *   const api = useSprints360Api();
 *   const { atividades } = await api.atividades.listar(projetoId);
 */
import axios from "axios";
import { useMemo } from "react";
import { getAccessToken } from "@/lib/supabase";
import { impHeaders } from "@/lib/impersonation";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Jun/2026 — Contador de ESCRITAS em voo. A fila de sincronização do
// Sprints360Context sabe o que ainda não foi enviado, mas não o que já saiu e
// ainda não voltou. Sem isso, o "Salvar antes de sair" declararia tudo gravado
// com requisições ainda no ar.
let writesInFlight = 0;
const METODOS_ESCRITA = new Set(["post", "patch", "put", "delete"]);
export function getWritesInFlight() { return writesInFlight; }

async function authedRequest(method, path, { params, data, timeout } = {}) {
  const token = await getAccessToken().catch(() => null);
  if (!token) throw new Error("Sem sessão autenticada — faça login para acessar Luoggo Projetos.");
  const config = {
    method,
    url: `${API_BASE}/api/sprints360${path}`,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...impHeaders() },
    timeout: timeout || 20000,
    params,
    data,
  };
  const escrita = METODOS_ESCRITA.has(String(method).toLowerCase());
  if (escrita) writesInFlight += 1;
  try {
    const r = await axios(config);
    return r.data;
  } finally {
    if (escrita) writesInFlight = Math.max(0, writesInFlight - 1);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-clients por recurso
// ─────────────────────────────────────────────────────────────────────────────
const atividadesClient = {
  listar: (projetoId, filtros = {}) =>
    authedRequest("get", "/atividades", { params: { projeto_id: projetoId, ...filtros } }),
  criar: (payload) => authedRequest("post", "/atividades", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/atividades/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/atividades/${id}`),
  // Out/2026 — reposicionamento manual da coluna "#". Deliberadamente
  // AWAITED (não fire-and-forget) pelo chamador — ver nota em
  // Sprints360Context.jsx::reordenarAtividade.
  reordenar: (id, novaPosicao) =>
    authedRequest("post", `/atividades/${id}/reordenar`, { data: { nova_posicao: novaPosicao } }),
  // Phase 12.75 — anotações rich-text por atividade (tabela dedicada)
  anotacoesGet: (id) => authedRequest("get", `/atividades/${id}/anotacoes`),
  anotacoesSave: (id, corpo_html) => authedRequest("put", `/atividades/${id}/anotacoes`, { data: { corpo_html } }),
};

// Phase 12.24 — Mapeamento de status UI → Backend.
// O frontend usa vocabulário histórico ('a_iniciar', 'em_execucao', ...),
// mas o backend valida contra ('pendente','em_andamento','bloqueada','atrasada',
// 'concluida','cancelada'). Aplicar em TODOS os payloads antes de enviar.
const STATUS_UI_TO_BACKEND = {
  a_iniciar: "pendente",
  nao_iniciada: "pendente",
  em_execucao: "em_andamento",
  em_andamento: "em_andamento",
  em_progresso: "em_andamento",
  concluida: "concluida",
  concluído: "concluida",
  concluido: "concluida",
  // Phase 12.80 — Bugfix: as chaves canônicas da UI (STATUS_LIST em
  // sb06Params.js, usadas pelo Kanban/SB-06) são no MASCULINO
  // (bloqueado/atrasado/cancelado), mas só existiam entradas no feminino
  // aqui. Qualquer save com esses 3 status (ou "standby") não encontrava a
  // chave, caía no fallback `|| "pendente"` e era persistido silenciosamente
  // como "pendente" — revertendo visualmente para "A iniciar" no próximo
  // refresh/hidratação, mesmo sem relação alguma com predecessoras.
  bloqueado: "bloqueada",
  bloqueada: "bloqueada",
  atrasado: "atrasada",
  atrasada: "atrasada",
  cancelado: "cancelada",
  cancelada: "cancelada",
  // "standby" não existia como valor aceito no backend (CHECK constraint de
  // `sprints.atividades.status`) — liberado via migração
  // `sql/migrations/2026_10_atividade_status_standby.sql`.
  standby: "standby",
  pendente: "pendente",
};

// Phase 12.56 — Reverso: usado ao hidratar do backend para que a UI (Select,
// STATUS_LIST) reconheça o valor. Sem isso o campo Status no detalhe da
// atividade ficava em branco pois `pendente` não é chave em STATUS_LIST.
const STATUS_BACKEND_TO_UI = {
  pendente:     "a_iniciar",
  em_andamento: "em_andamento",
  bloqueada:    "bloqueado",
  atrasada:     "atrasado",
  concluida:    "concluido",
  cancelada:    "cancelado",
  standby:      "standby",
};

export function mapStatusUiToBackend(status) {
  if (!status) return "pendente";
  const key = String(status).toLowerCase().trim();
  return STATUS_UI_TO_BACKEND[key] || "pendente";
}

export function mapStatusBackendToUi(status) {
  if (!status) return "a_iniciar";
  const key = String(status).toLowerCase().trim();
  return STATUS_BACKEND_TO_UI[key] || key;
}

const dependenciasClient = {
  listar: (projetoId) => authedRequest("get", "/dependencias", { params: { projeto_id: projetoId } }),
  criar: (payload) => authedRequest("post", "/dependencias", { data: payload }),
  bulk: (projetoId, dependencias) =>
    authedRequest("post", "/dependencias/bulk", { data: { projeto_id: projetoId, dependencias } }),
  excluir: (id) => authedRequest("delete", `/dependencias/${id}`),
};

const participantesClient = {
  listar: (projetoId, apenasAtivos = true) =>
    authedRequest("get", "/participantes", { params: { projeto_id: projetoId, apenas_ativos: apenasAtivos } }),
  criar: (payload) => authedRequest("post", "/participantes", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/participantes/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/participantes/${id}`),
};

const rituaisClient = {
  listar: (projetoId, apenasPendentes = false) =>
    authedRequest("get", "/rituais", { params: { projeto_id: projetoId, apenas_pendentes: apenasPendentes } }),
  criar: (payload) => authedRequest("post", "/rituais", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/rituais/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/rituais/${id}`),
};

const atasClient = {
  listar: (projetoId) => authedRequest("get", "/atas", { params: { projeto_id: projetoId } }),
  criar: (payload) => authedRequest("post", "/atas", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/atas/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/atas/${id}`),
};

const documentosClient = {
  listar: (projetoId, tipo = null) =>
    authedRequest("get", "/documentos", { params: { projeto_id: projetoId, ...(tipo ? { tipo } : {}) } }),
  criar: (payload) => authedRequest("post", "/documentos", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/documentos/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/documentos/${id}`),
};

// Phase 12.20 — write-through para versões/sprints/etapas/negócios
const versoesClient = {
  listar: (projetoId) => authedRequest("get", "/versoes", { params: { projeto_id: projetoId } }),
  criar: (payload) => authedRequest("post", "/versoes", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/versoes/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/versoes/${id}`),
};

const sprintsClient = {
  listar: (projetoId, versaoId = null) =>
    authedRequest("get", "/sprints", { params: { projeto_id: projetoId, ...(versaoId ? { versao_id: versaoId } : {}) } }),
  criar: (payload) => authedRequest("post", "/sprints", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/sprints/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/sprints/${id}`),
};

const etapasClient = {
  listar: (projetoId, versaoId = null) =>
    authedRequest("get", "/etapas", { params: { projeto_id: projetoId, ...(versaoId ? { versao_id: versaoId } : {}) } }),
  criar: (payload) => authedRequest("post", "/etapas", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/etapas/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/etapas/${id}`),
};

const negociosClient = {
  listar: (projetoId, versaoId = null) =>
    authedRequest("get", "/negocios", { params: { projeto_id: projetoId, ...(versaoId ? { versao_id: versaoId } : {}) } }),
  criar: (payload) => authedRequest("post", "/negocios", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/negocios/${id}`, { data: patch }),
  excluir: (id) => authedRequest("delete", `/negocios/${id}`),
};

const projetosClient = {
  // Phase 12.27 — aceita filtro por empresa simulada e/ou modo consultoria OFF.
  listar: (opts = {}) => {
    const params = {};
    if (opts.empresa_simulada_id) params.empresa_simulada_id = opts.empresa_simulada_id;
    if (opts.modo_consultoria === false) params.modo_consultoria = false;
    return authedRequest("get", "/projetos", { params });
  },
  criar: (payload) => authedRequest("post", "/projetos", { data: payload }),
  atualizar: (id, patch) => authedRequest("patch", `/projetos/${id}`, { data: patch }),
  autoSeed: () => authedRequest("post", "/projetos/auto-seed"),
  excluir: (id) => authedRequest("delete", `/projetos/${id}`),
};

// Fev/2026 — Item 3 da análise de performance: snapshot único do portfólio
// inteiro (ver `routes/sprints_portfolio.py`), substituindo as ~10 chamadas
// por projeto que a hidratação completa fazia antes.
const portfolioClient = {
  snapshot: (opts = {}) => {
    const params = {};
    if (opts.empresa_simulada_id) params.empresa_simulada_id = opts.empresa_simulada_id;
    if (opts.modo_consultoria === false) params.modo_consultoria = false;
    return authedRequest("get", "/portfolio/snapshot", { params, timeout: 30000 });
  },
};

/** Retorna o cliente unificado (todos os sub-clients agrupados). */
export function useSprints360Api() {
  return useMemo(() => ({
    projetos: projetosClient,
    atividades: atividadesClient,
    dependencias: dependenciasClient,
    participantes: participantesClient,
    rituais: rituaisClient,
    atas: atasClient,
    documentos: documentosClient,
    versoes: versoesClient,
    sprints: sprintsClient,
    etapas: etapasClient,
    negocios: negociosClient,
  }), []);
}

// Phase 12.56 · SB-16 — Simulação de Cronograma Pré-Criação
// Phase 12.60 · timeout estendido (90s) porque a chamada a Claude Haiku para
// dezenas de atividades pode levar 30-60s. O timeout default (20s) causava
// erro "timeout of 20000ms exceeded" e mantinha o modal em fallback 16h/atividade.
const simClient = {
  estimarWorkload: (body) =>
    authedRequest("post", "/sim/estimar-workload", { data: body, timeout: 90000 }),
};

// Phase 12.9 · Também expõe os sub-clients diretamente (útil fora de hooks,
// ex.: dentro do Sprints360Context para write-through).
export const sprints360Api = {
  projetos: projetosClient,
  atividades: atividadesClient,
  dependencias: dependenciasClient,
  participantes: participantesClient,
  rituais: rituaisClient,
  atas: atasClient,
  documentos: documentosClient,
  versoes: versoesClient,
  sprints: sprintsClient,
  etapas: etapasClient,
  negocios: negociosClient,
  sim: simClient,
};

// Phase 12.80 — Busca leve (2 requests, só desta atividade/projeto) usada
// pelo write-through do Context para re-confirmar o estado REAL do backend
// depois de um save que toca `depende_de` (create/delete de dependências).
// Diferente de `hydrateSprints360FromBackend` (todos os projetos, lento),
// esta busca só o necessário para fechar a janela entre "salvei" e "o
// backend já reflete o que salvei", sem esperar a próxima hidratação geral.
export async function fetchAtividadesDoProjetoComDeps(projetoId) {
  const [atividadesResp, depsResp] = await Promise.all([
    atividadesClient.listar(projetoId).then(r => r.atividades || []).catch(() => []),
    dependenciasClient.listar(projetoId).then(r => r.dependencias || []).catch(() => []),
  ]);
  const dePorAtividade = new Map();
  for (const d of depsResp) {
    const arr = dePorAtividade.get(d.atividade_destino_id) || [];
    arr.push(d.atividade_origem_id);
    dePorAtividade.set(d.atividade_destino_id, arr);
  }
  return atividadesResp.map(a => {
    const local = atividadeBackendToLocal(a);
    local.depende_de = dePorAtividade.get(a.id) || [];
    return local;
  });
}

/** Snapshot completo do projeto — útil para alimentar a Val ou dashboards. */
export async function fetchSnapshotProjeto(projetoId) {
  const [atividades, dependencias, participantes, rituais, atas, documentos] = await Promise.all([
    atividadesClient.listar(projetoId).then((r) => r.atividades).catch(() => []),
    dependenciasClient.listar(projetoId).then((r) => r.dependencias).catch(() => []),
    participantesClient.listar(projetoId).then((r) => r.participantes).catch(() => []),
    rituaisClient.listar(projetoId).then((r) => r.rituais).catch(() => []),
    atasClient.listar(projetoId).then((r) => r.atas).catch(() => []),
    documentosClient.listar(projetoId).then((r) => r.documentos).catch(() => []),
  ]);
  return { atividades, dependencias, participantes, rituais, atas, documentos };
}

// =====================================================================
// Phase 12.9 · Adaptadores backend ↔ shape do Sprints360Context
// =====================================================================
/**
 * Converte o payload de `GET /sprints360/projetos` para o formato usado no
 * reducer local (`state.projetos[i]`). Mantém `_origem: "backend"` para o
 * write-through futuro saber que este item já existe no banco.
 */
export function projetoBackendToLocal(p) {
  const di = p.data_inicio ? new Date(p.data_inicio) : null;
  const df = p.data_fim_prevista ? new Date(p.data_fim_prevista) : null;
  const duracao_dias = (di && df && !isNaN(di) && !isNaN(df))
    ? Math.max(1, Math.round((df - di) / 86400000))
    : null;
  const out = {
    id: p.id,
    empresa_id: p.empresa_id,
    codigo: p.codigo,
    nome: p.nome,
    tipo_projeto: p.tipo_projeto,
    status: p.status,
    cor: p.cor,
    sponsor_nome: p.sponsor_nome,
    gp_nome: p.gp_nome,
    data_inicio_prevista: p.data_inicio || null,
    cadencia_dias: p.cadencia_dias,
    contexto_livre: p.contexto_livre,
    empresa_simulada_id: p.empresa_simulada_id || null,  // Phase 12.27
    _origem: "backend",
    criado_em: p.created_at,
    atualizado_em: p.updated_at,
    // Jun/2026 — `updated_at` canônico. O merge da hidratação
    // (`mergeById`) compara `cur.updated_at` × `rem.updated_at` para decidir
    // se preserva a edição local. Antes só existia `atualizado_em`, então a
    // comparação sempre dava falso e o backend SEMPRE ganhava — revertendo
    // silenciosamente cada edição do usuário a cada re-hidratação.
    updated_at: p.updated_at,
  };
  // Jun/2026 — Campos que o backend NÃO persiste não podem ir no payload:
  // com a chave presente (mesmo null/false/[]), o spread `{...local, ...remoto}`
  // do merge apaga o valor local. Omitindo a chave, o local sobrevive.
  //   • sprint_ativado / versoes / participantes → não existem em sprints.projetos
  //   • data_fim_prevista / duracao_dias → só entram se o backend tiver valor
  if (p.data_fim_prevista) out.data_fim_prevista = p.data_fim_prevista;
  if (duracao_dias != null) out.duracao_dias = duracao_dias;
  return out;
}

/**
 * Converte atividade do backend para o shape do reducer local.
 * Mapeia `horas_est/horas_real` → `horas_estimadas/horas_realizadas`, e
 * garante fallback `data_inicio_prevista ← data_inicio` (compat colunas
 * antigas).
 */
export function atividadeBackendToLocal(a) {
  return {
    id: a.id,
    projeto_id: a.projeto_id,
    titulo: a.titulo,
    descricao: a.descricao,
    // Phase 12.56 — Sempre converte para chave UI (`a_iniciar`, `em_andamento`, …)
    status: mapStatusBackendToUi(a.status),
    // Phase 12.55.19 — Espelha campos canônicos (`data_inicio`/`data_fim`) a
    // partir dos "_prevista", que são o que o backend efetivamente armazena
    // (a coluna `data_fim` nem existe no schema). Sem isso, o valor canônico
    // vinha `undefined` após cada hidratação e qualquer código que lesse
    // `a.data_fim` (Gantt, revalidador, deltas de duração) tratava a
    // atividade como "sem fim conhecido" e recalculava datas erradas.
    data_inicio: a.data_inicio || a.data_inicio_prevista || null,
    data_inicio_prevista: a.data_inicio_prevista || a.data_inicio || null,
    data_inicio_real: a.data_inicio_real || null,
    data_fim: a.data_fim_prevista || null,
    data_fim_prevista: a.data_fim_prevista || null,
    data_fim_real: a.data_fim_real || null,
    horas_estimadas: a.horas_est,
    horas_realizadas: a.horas_real,
    percentual_concluido: a.percentual_concluido,
    responsavel_id: a.responsavel_id,
    responsavel_nome: a.responsavel_nome,
    profissional_nome: a.responsavel_nome,   // Alias lido pela tabela detalhe (SB-06)
    sprint_num: a.sprint_num,
    // Phase 12.32 — FKs organizacionais persistidas no backend
    sprint_id:  a.sprint_id  || null,
    etapa_id:   a.etapa_id   || null,
    negocio_id: a.negocio_id || null,
    // Jun/2026 — versionamento persistido (coluna aditiva `versao_id`)
    versao_id:  a.versao_id  || null,
    tags: a.tags,
    // Fev/2026 — nível do profissional (Pleno/Sênior/Júnior), agora persistido.
    nivel: a.nivel || null,
    _origem: "backend",
    created_at: a.created_at,
    updated_at: a.updated_at,
    // Out/2026 — posição sequencial persistida (fonte única da coluna "#").
    numero: a.ordem_sequencial ?? null,
  };
}

export function participanteBackendToLocal(p) {
  return {
    id: p.id,
    projeto_id: p.projeto_id,
    nome: p.nome,
    papel: p.papel,
    alocacao_semanal_horas: p.alocacao_semanal_horas,
    capacidade_semanal_horas: p.capacidade_semanal_horas,
    projetos_paralelos: p.projetos_paralelos,
    usuario_id: p.usuario_id,
    _origem: "backend",
  };
}

export function versaoBackendToLocal(v) {
  return {
    id: v.id,
    projeto_id: v.projeto_id,
    numero: v.numero,
    rotulo: v.rotulo,
    rotulo_original: v.rotulo_original,
    descricao: v.descricao,
    status: v.status,
    arquivada: v.arquivada,
    finalized_at: v.finalized_at,
    created_at: v.created_at,
    _origem: "backend",
  };
}

export function sprintBackendToLocal(s) {
  return {
    id: s.id,
    projeto_id: s.projeto_id,
    versao_id: s.versao_id,
    numero: s.numero,
    nome: s.nome,
    descricao: s.descricao,
    data_inicio: s.data_inicio,
    data_fim: s.data_fim,
    ativo: s.ativo,
    _origem: "backend",
  };
}

export function etapaBackendToLocal(e) {
  return {
    id: e.id,
    projeto_id: e.projeto_id,
    versao_id: e.versao_id,
    ordem: e.ordem,
    nome: e.nome,
    cor: e.cor,
    _origem: "backend",
  };
}

export function negocioBackendToLocal(n) {
  return {
    id: n.id,
    projeto_id: n.projeto_id,
    versao_id: n.versao_id,
    nome: n.nome,
    origem: n.origem,
    _origem: "backend",
  };
}

/**
 * Hidratação completa: snapshot único do portfólio inteiro, já em shape do
 * reducer. Retorna { projetos, atividades, participantes, rituais, versoes,
 * sprints, etapas, negocios }.
 *
 * Fev/2026 — Item 3 da análise de performance ("Projetos abre o projeto
 * errado" / "Dashboard mostra 0"). Antes, hidratar N projetos fazia N × 10
 * chamadas HTTP (uma por tabela POR projeto), cada uma repetindo a
 * autorização — medido em ~19-30s para 17 projetos. Agora é UMA chamada
 * (`GET /sprints360/portfolio/snapshot`) que já traz todos os projetos e
 * todas as tabelas de uma vez; o agrupamento por projeto (numeração de
 * atividades, dependências, versão ativa) continua sendo feito aqui, no
 * cliente, preservando exatamente a mesma lógica/; resultado de antes.
 *
 * Jun/2026 — `onProjetos` é chamado ASSIM QUE a lista de projetos é
 * conhecida (agora, no mesmo retorno do snapshot — não há mais um segundo
 * estágio "detalhes chegam depois" por projeto).
 * Erros sobem para o chamador (o Provider faz retry).
 */
export async function hydrateSprints360FromBackend(opts = {}, onProjetos = null) {
  const empty = { projetos: [], atividades: [], participantes: [], rituais: [], versoes: [], sprints: [], etapas: [], negocios: [] };
  try {
    const snap = await portfolioClient.snapshot(opts);
    const projetos = snap.projetos || [];
    if (projetos.length === 0) return empty;
    const projetosLocal = projetos.map(projetoBackendToLocal);
    if (typeof onProjetos === "function") onProjetos(projetosLocal);

    // destino_id → [origem_id, …]. IDs de atividade são UUIDs globais —
    // não há colisão entre projetos, então o mapa pode ser único.
    const dePorAtividade = new Map();
    for (const d of (snap.dependencias || [])) {
      const arr = dePorAtividade.get(d.atividade_destino_id) || [];
      arr.push(d.atividade_origem_id);
      dePorAtividade.set(d.atividade_destino_id, arr);
    }

    // Agrupa atividades por projeto para preservar a MESMA ordenação e
    // numeração (fallback por índice) que existia quando cada projeto
    // chegava em um snapshot separado.
    const porProjeto = new Map();
    for (const a of (snap.atividades || [])) {
      const arr = porProjeto.get(a.projeto_id) || [];
      arr.push(a);
      porProjeto.set(a.projeto_id, arr);
    }
    const atividades = [];
    for (const lista of porProjeto.values()) {
      const ordenadas = [...lista].sort((x, y) => {
        const nx = x.ordem_sequencial, ny = y.ordem_sequencial;
        if (nx != null && ny != null && nx !== ny) return nx - ny;
        const cx = x.created_at || ""; const cy = y.created_at || "";
        if (cx !== cy) return cx < cy ? -1 : 1;
        return String(x.id).localeCompare(String(y.id));
      });
      ordenadas.forEach((a, i) => {
        const local = atividadeBackendToLocal(a);
        local.numero = a.ordem_sequencial ?? (i + 1);
        local.depende_de = dePorAtividade.get(a.id) || [];
        atividades.push(local);
      });
    }

    const participantes = (snap.participantes || []).map(participanteBackendToLocal);
    const rituais = snap.rituais || [];
    const versoes = (snap.versoes || []).map(versaoBackendToLocal);
    const sprintsAcc = (snap.sprints || []).map(sprintBackendToLocal);
    const etapasAcc = (snap.etapas || []).map(etapaBackendToLocal);
    const negociosAcc = (snap.negocios || []).map(negocioBackendToLocal);

    // Preenche `versao_ativa_id` no projeto (última versão não-arquivada, ou a mais recente)
    for (const p of projetosLocal) {
      const vs = versoes.filter(v => v.projeto_id === p.id);
      const finalizadaAtiva = vs.find(v => v.status === "finalizado" && !v.arquivada);
      const rascunhoAtivo   = vs.find(v => v.status === "rascunho" && !v.arquivada);
      const escolhida = finalizadaAtiva || rascunhoAtivo || vs[vs.length - 1];
      if (escolhida) p.versao_ativa_id = escolhida.id;
    }
    return { projetos: projetosLocal, atividades, participantes, rituais, versoes, sprints: sprintsAcc, etapas: etapasAcc, negocios: negociosAcc };
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[sprints360] hydrateFromBackend falhou:", err?.message || err);
    // Jun/2026 — ANTES devolvíamos `empty` aqui. Isso tornava a falha
    // indistinguível de "tenant sem projeto": o Sprints360 abria vazio e não
    // havia retry. Agora o erro sobe para o Provider decidir (retry/aviso).
    throw err;
  }
}
